import Decimal from 'decimal.js'
import type { ArticleLocal, ClientLocal, DevisLocal } from '../../outbox/base'
import { LIBELLES_STATUT } from '../devis'
import { centimesVersEuros, totalDocument, totalLigne } from '../money'
import { versCalcul } from '../pdf/devis-pdf'
import type { FeuilleExport } from './classeur'
import type { Cellule } from './lecture'
import { CHAMPS, type ResultatLigne, type TypeImport } from './import'

export type { FeuilleExport }

// Exports (classeur complet, CSV), fichiers modèles et rapport d'import.
// Les en-têtes reprennent les libellés de l'import : un export se réimporte tel quel.

const entetes = (type: TypeImport) => CHAMPS[type].map((c) => c.libelle)
// Montants exportés en euros (nombres Excel) : la conversion se fait sur des centimes exacts
const euros = (cents: number | undefined) => (cents === undefined ? null : centimesVersEuros(cents).toNumber())
const nombre = (d: Decimal) => d.toNumber()

export function feuilleArticles(articles: ArticleLocal[]): FeuilleExport {
  return {
    nom: 'Articles',
    lignes: [
      entetes('articles'),
      ...articles.map((a) => [
        a.reference ?? null,
        a.designation,
        a.unite,
        euros(a.prixAchatHtCents),
        a.coefficientMarge ?? null,
        euros(a.prixVenteHtCents),
        a.tvaPercent,
      ]),
    ],
  }
}

export function feuilleClients(clients: ClientLocal[]): FeuilleExport {
  return { nom: 'Clients', lignes: [entetes('clients'), ...clients.map((c) => [c.nom, c.telephone ?? null, c.email ?? null, c.adresse ?? null, c.codePostal ?? null, c.ville ?? null, c.siret ?? null])] }
}

/** Devis (une ligne par devis, totaux) et lignes de devis (une ligne par prestation). */
export function feuillesDevis(devis: DevisLocal[], clients: ClientLocal[]): FeuilleExport[] {
  const noms = new Map(clients.map((c) => [c.id, c.nom]))
  const tries = [...devis].sort((a, b) => a.id.localeCompare(b.id))
  return [
    {
      nom: 'Devis',
      lignes: [
        ['Numéro', 'Statut', 'Client', 'Modifié le', 'Total HT', 'TVA', 'Total TTC'],
        ...tries.map((d) => {
          const t = totalDocument(d.lignes.map(versCalcul))
          return [d.numero, LIBELLES_STATUT[d.statut], noms.get(d.clientId) ?? null, d.updatedAt.slice(0, 10), nombre(t.ht), nombre(t.tva), nombre(t.ttc)]
        }),
      ],
    },
    {
      nom: 'Lignes de devis',
      lignes: [
        ['Numéro du devis', 'Désignation', 'Unité', 'Quantité', 'Prix unitaire HT', 'TVA (%)', 'Total HT'],
        ...tries.flatMap((d) =>
          d.lignes.map((l) => [
            d.numero,
            l.designation,
            l.unite ?? null,
            l.quantite,
            euros(l.prixUnitaireHtCents),
            l.tvaPercent,
            nombre(totalLigne(versCalcul(l)).ht),
          ]),
        ),
      ],
    },
  ]
}

/** Fichier modèle : en-têtes reconnus par l'import et deux exemples. */
export function feuilleModele(type: TypeImport): FeuilleExport {
  if (type === 'clients') {
    return { nom: 'Clients', lignes: [entetes('clients'), ['Syndic Les Tilleuls', '0601020304'], ['Mme Durand', '0478123456']] }
  }
  return {
    nom: 'Articles',
    lignes: [
      entetes('articles'),
      ['CU22', 'Tube cuivre Ø22', 'ml', 8.4, 1.35, 11.34, 20],
      [null, 'Remplacement circulateur', 'forfait', null, null, 245, 10],
    ],
  }
}

/**
 * Injection de formules (CSV injection) : un texte qui commence par = + - @ (ou une tabulation, un retour) serait
 * exécuté comme une formule par Excel ou LibreOffice à l'ouverture (`=HYPERLINK(…)` dans une note de pointage, un nom
 * de client…). Préfixé d'une apostrophe, il reste du texte. L'import retire cette apostrophe (`lecture.ts`).
 */
export const neutraliserFormule = (texte: string) => (/^[=+\-@\t\r]/.test(texte) ? `'${texte}` : texte)

/** CSV pour Excel en français : point-virgule, virgule décimale, BOM UTF-8 (accents). */
export function versCsv(feuille: FeuilleExport): string {
  // Échappement fait ici : Papa Parse resterait sinon dans le bundle principal
  const cellule = (c: Cellule) => {
    const s = typeof c === 'number' ? String(c).replace('.', ',') : c === null ? '' : neutraliserFormule(String(c))
    return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return '\uFEFF' + feuille.lignes.map((l) => l.map(cellule).join(';')).join('\r\n')
}

export function feuilleRapport(resultats: ResultatLigne[]): FeuilleExport {
  const libelles = { cree: 'Importée', mis_a_jour: 'Mise à jour', ignore: 'Ignorée', erreur: 'En erreur' }
  return {
    nom: 'Rapport',
    lignes: [['Ligne', 'Libellé', 'Résultat', 'Détail'], ...resultats.map((r) => [r.numero, r.libelle, libelles[r.resultat], r.message])],
  }
}
